import * as APIRoutes from '@mm/api-routes';
import { Contact, SelectedContact } from '../interfaces/contacts';
import { MyLeadsAttributes } from '../interfaces/myLeads';
import { FilterParams, TagsFilters } from '../model/AudiencesModel';
import { ContactsModel } from '../model/ContactsModel';
import { CaptureActionEnum } from '../model/SuppressionSettingsModel';
import AuthenticatedController from './AuthenticatedController';

type HotLeadsResponse = {
  hotLeads: string[];
};

export type ContactsCSVType = {
  id: number;
  fileName: string;
  sourceSize: number;
  uploadUrl: string;
  uploaded: boolean;
  recordsCount?: number;
  headersAtFirstLine: boolean;
  processed: boolean;
  processedPercentage: number;
  mapping: { [key: string]: string };
  createdAt: string;
  updatedAt: string;
  progressbar: {
    audienceId: number;
    percentage: number;
  };
  captureAction: CaptureActionEnum;
};

export type ContactsCsvImportJobResponse = {
  success: boolean;
  errors: string;
  contactsCsv: ContactsCSVType;
};

export type CsvImportOptions = {
  facebookPageId: number;
  fileName: string;
  sourceSize: number;
  mapping: { [key: string]: string };
  headersAtFirstLine: boolean;
  captureAction?: CaptureActionEnum;
};

export type ExportableContactField = { name: string; value: string };

export type UpdateContact = Partial<Omit<Contact, 'id'>> & { id: number };

export default class ContactsController extends AuthenticatedController {
  static async getAll({
    pageId,
    pageNumber = 0,
    pageSize = 25,
    query,
    sortColumn,
    sortDirection,
    filterParams,
    tagsFilters,
    esFilters,
    withDeliverable,
  }: {
    pageId: number;
    pageNumber?: number;
    pageSize?: number;
    query?: string;
    sortColumn?: string;
    sortDirection?: 'asc' | 'desc';
    filterParams?: FilterParams;
    tagsFilters?: TagsFilters;
    esFilters?: MyLeadsAttributes;
    withDeliverable?: boolean;
  }): Promise<ContactsModel> {
    const queryParams = `?${new URLSearchParams({
      ...(query ? { query } : {}),
      ...(Object.keys(filterParams || {}).length && {
        filter_params: JSON.stringify(filterParams),
      }),
      ...(Object.keys(esFilters || {}).length && {
        es_filters: JSON.stringify({ query: esFilters }),
      }),
      ...(Object.keys(tagsFilters || {}).length && {
        tags_filters: JSON.stringify(tagsFilters),
      }),
      page: String(pageNumber),
      per_page: String(pageSize),
      ...(sortColumn ? { sort_column: sortColumn } : {}),
      ...(sortDirection ? { sort_direction: sortDirection } : {}),
      ...(withDeliverable && { with_deliverable: withDeliverable?.toString() }),
    } as Record<string, string>).toString()}`;

    return super.get(`${APIRoutes.FB_PAGE_CONTACTS(pageId)}${queryParams}`);
  }

  static getAllCSV(companyId: number, connectedPageId: number) {
    return super.get(APIRoutes.FB_PAGE_CONTACT_CSV(connectedPageId));
  }

  static getContactById(
    companyId: number,
    contactId: number,
    pageId: number,
  ): Promise<SelectedContact> {
    return Promise.all([
      super.get(
        `${APIRoutes.CONTACTS}/${contactId}?facebook_page_id=${pageId}`,
      ),
      super.get(`${APIRoutes.CONTACTS}/${contactId}/sessions`),
      super.get(`${APIRoutes.CONTACTS}/${contactId}/variable_values`),
      super.get(`${APIRoutes.CONTACTS}/${contactId}/contact_notes`),
      super.get(
        `${APIRoutes.CONTACTS}/${contactId}/lead_scoring?facebook_page_id=${pageId}`,
      ),
      super.get(
        `${APIRoutes.CONTACTS}/${contactId}/customer_journey?facebook_page_id=${pageId}`,
      ),
    ]).then(
      ([
        contactResponse,
        sessionsResponse,
        variablesResponse,
        notesResponse,
        leadScoringResponse,
        customerJourneyResponse,
      ]): SelectedContact => ({
        ...contactResponse,
        notes: notesResponse.notes,
        sessions: sessionsResponse.sessions,
        variables: variablesResponse.variableValues,
        leadScoring: leadScoringResponse,
        customerJourney: customerJourneyResponse,
      }),
    );
  }

  static async getContactByRemoteId(
    remoteId: string,
    pageId: number,
  ): Promise<SelectedContact> {
    const contactResponse: Contact = await super.get(
      `${APIRoutes.FB_PAGE_CONTACTS_REMOTE_ID(pageId, remoteId)}`,
    );
    const [
      sessionsResponse,
      variablesResponse,
      notesResponse,
      leadScoringResponse,
      customerJourneyResponse,
    ] = await Promise.all([
      super.get(
        `${APIRoutes.CONTACTS}/${contactResponse.id}/sessions?live_chat=true`,
      ),
      super.get(`${APIRoutes.CONTACTS}/${contactResponse.id}/variable_values`),
      super.get(`${APIRoutes.CONTACTS}/${contactResponse.id}/contact_notes`),
      super.get(
        `${APIRoutes.CONTACTS}/${contactResponse.id}/lead_scoring?facebook_page_id=${pageId}`,
      ),
      super.get(
        `${APIRoutes.CONTACTS}/${contactResponse.id}/customer_journey?facebook_page_id=${pageId}`,
      ),
    ]);

    return {
      ...contactResponse,
      notes: notesResponse.notes,
      sessions: sessionsResponse.sessions,
      variables: variablesResponse.variableValues,
      leadScoring: leadScoringResponse,
      customerJourney: customerJourneyResponse,
    };
  }

  static deleteSelectedContact(
    connectedPageId: number,
    contactId: number,
    pageNumber?: number,
    pageSize?: number,
    query?: string,
    eraseRemoteMessages?: boolean,
  ) {
    const queryParams = `?${new URLSearchParams({
      facebook_page_id: String(connectedPageId),
      ...(eraseRemoteMessages ? { erase_remote_messages: 'true' } : {}),
    }).toString()}`;
    return super
      .delete(`${APIRoutes.CONTACTS}/${contactId}${queryParams}`)
      .then(() =>
        this.getAll({ pageId: connectedPageId, pageNumber, pageSize, query }),
      );
  }

  static deleteBatchedContacts(fbPageId: number, contactsIds: number[]) {
    return super.delete(
      APIRoutes.CONTACTS_BATCH_DELETE(fbPageId),
      undefined,
      undefined,
      {
        contact_ids: contactsIds,
      },
    );
  }

  static createScheduledExport(
    connectedPageId: number,
    params?: {
      emailAddress?: string;
      googleSheetId?: string;
      googleSheetTitle?: string;
      googleSpreadsheetId?: string;
      googleSpreadsheetTitle?: string;
      googleAccountId?: number;
      fieldsForExport?: string[];
    },
  ) {
    return super.post(
      APIRoutes.FB_PAGE_CONTACT_EXPORT(connectedPageId),
      params,
    );
  }

  static getExportableFields(
    connectedPageId: number,
  ): Promise<ExportableContactField[]> {
    return super.get(APIRoutes.FB_PAGE_CONTACT_EXPORT_FIELDS(connectedPageId));
  }

  static getHotLeads(connectedPageId: number): Promise<HotLeadsResponse> {
    return super.get(
      `${APIRoutes.FB_PAGE_CONTACTS(connectedPageId)}/hot_leads`,
    );
  }

  static createContactsCsvImportJob(
    options: CsvImportOptions,
  ): Promise<ContactsCsvImportJobResponse> {
    return super.post(APIRoutes.IMPORT_CONTACTS_CSV_JOB, options);
  }

  static updateContactsCsvImportJob(id: number, uploaded: boolean) {
    return super.put(`${APIRoutes.IMPORT_CONTACTS_CSV_JOB}/${id}`, {
      uploaded,
    });
  }

  static getContactsCsvImportJob(
    id: number,
  ): Promise<ContactsCsvImportJobResponse> {
    return super.get(`${APIRoutes.IMPORT_CONTACTS_CSV_JOB}/${id}`);
  }

  static uploadContactsCsvFile(uploadUrl: string, file: File) {
    const request = new Request(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });
    return fetch(request);
  }

  static update(
    facebookPageId: string,
    contact: UpdateContact,
  ): Promise<Contact> {
    return super.put(
      `${APIRoutes.CONTACTS}/${contact.id}?facebook_page_id=${facebookPageId}`,
      contact,
    );
  }

  static getContactsMostVisistedUrls(
    facebookPageId: number,
  ): Promise<string[]> {
    return super.get(APIRoutes.CONTACTS_MOST_VISITED_WEB_URLS(facebookPageId));
  }
}
